import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { vendorShops } from "@/lib/vendorApi";
import { fetchMyActivities, fetchAdSettings, fetchAdModeNotice, dismissAdModeNotice } from "@/lib/api";
import { AdModeNotice } from "@/components/vendor/AdModeNotice";
import { Store, Package, Wrench, BarChart3, Megaphone, Settings, ArrowLeft, ExternalLink, Briefcase, ChevronDown } from "lucide-react";
import ShopProfileEditor from "@/components/vendor/ShopProfileEditor";
import ShopStatusCard from "@/components/vendor/ShopStatusCard";
import BusinessProfileEditor from "@/components/vendor/BusinessProfileEditor";
import { LocalDemandCard } from "@/components/vendor/LocalDemandCard";
import { DashboardTop } from "@/components/vendor/DashboardTop";
import MyProducts from "@/components/vendor/MyProducts";
import MyServices from "@/components/vendor/MyServices";
import VendorAnalytics from "@/components/vendor/VendorAnalytics";
import AddProductDialog from "@/components/vendor/AddProductDialog";
import AdvertiseDialog, { VendorAdsList } from "@/components/vendor/AdvertiseDialog";
import { useAdStrings } from "@/lib/adStrings";
import ShareSheet from "@/components/ShareSheet";
import VendorToolBag from "@/components/vendor/VendorToolBag";
import { T } from "@/i18n";
import { toast } from "sonner";
import { ReceivedLocations } from "@/components/maps/LocationShare";
import { SendLocationToCustomer } from "@/components/maps/SendToCustomer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "shop", label: "vendor.shop", icon: Store },
  { id: "products", label: "vendor.products", icon: Package },
  { id: "services", label: "vendor.services", icon: Wrench },
  { id: "ads", label: "vendor.ads", icon: Megaphone },
  { id: "analytics", label: "vendor.analytics", icon: BarChart3 },
];

const isCoord = (v, max) => typeof v === "number" && Number.isFinite(v) && Math.abs(v) <= max;
const hasValidLocation = (s) => isCoord(s.latitude, 90) && isCoord(s.longitude, 180) && !(s.latitude === 0 && s.longitude === 0);

export default function VendorDashboard() {
  const { user, isVendor, mode, setMode, authChecking } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const navKey = useLocation().key;
  const [shops, setShops] = useState([]);
  const [activeShopId, setActiveShopId] = useState(null);
  const AM = useAdStrings().mgr;
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    const valid = ["products", "analytics", "shop", "services", "ads"];
    return valid.includes(t) ? t : null;
  });
  const [addOpen, setAddOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [advertiseOpen, setAdvertiseOpen] = useState(false);
  const [adsRefreshKey, setAdsRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toolBagOpen, setToolBagOpen] = useState(false);
  const [serviceAddSignal, setServiceAddSignal] = useState(0);
  const [pro, setPro] = useState(null);
  const [toolBagPanel, setToolBagPanel] = useState({});
  const [freeAds, setFreeAds] = useState(false);
  useEffect(() => { fetchAdSettings().then((s) => setFreeAds(!!s?.free_mode)).catch(() => {}); }, []);
  const [modeNotice, setModeNotice] = useState(null);
  useEffect(() => { if (isVendor) fetchAdModeNotice().then(setModeNotice).catch(() => {}); }, [isVendor]);
  const closeModeNotice = () => { const id = modeNotice?.id; setModeNotice(null); if (id) dismissAdModeNotice(id).catch(() => {}); };
  const firstTab = useRef(true);
  useEffect(() => {
    if (firstTab.current) { firstTab.current = false; return; }
    if (tab) setTimeout(() => document.querySelector(`[data-testid="vendor-section-${tab}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  }, [tab]);

  // Deep link (?tab=ads from the menu): open that section and scroll once its position has settled after loading.
  const tabParam = searchParams.get("tab");
  useEffect(() => {
    if (loading || !["products", "analytics", "shop", "services", "ads"].includes(tabParam)) return undefined;
    setTab(tabParam);
    let tries = 0;
    let last = null;
    const poll = setInterval(() => {
      tries += 1;
      const el = document.querySelector(`[data-testid="vendor-section-${tabParam}"]`);
      if (!el) { if (tries > 30) clearInterval(poll); return; }
      const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
      if (top === last || tries > 30) { clearInterval(poll); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
      last = top;
    }, 150);
    return () => clearInterval(poll);
  }, [tabParam, loading, searchParams, navKey]); // navKey: re-run when the menu re-opens the same ?tab= link
  useEffect(() => {
    if (searchParams.get("toolbag") === "1") {
      setToolBagPanel({ panel: searchParams.get("panel"), ret: searchParams.get("return") });
      setToolBagOpen(true);
      ["toolbag", "panel", "return"].forEach((k) => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (authChecking) return;
    if (!user) { navigate("/", { replace: true }); return; }
    if (!isVendor) { toast.error("Vendor access only"); navigate("/", { replace: true }); return; }
    if (mode !== "vendor") setMode("vendor");
    vendorShops().then((list) => {
      setShops(list);
      if (!list.length) {
        fetchMyActivities().then((a) => {
          if (a?.activities?.includes("professional") || a?.professional_listing) setPro({ public: !!(a.professional_listing?.active || a.profiles?.professional?.published) });
        }).catch(() => {});
      }
      if (list.length && !activeShopId) setActiveShopId(list[0].shop_id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user, isVendor, authChecking, refreshKey]);

  // Owner "Set location" from the public shop page: /vendor?tab=shop&focus=location&shop=<id> opens the existing location picker.
  useEffect(() => {
    if (loading || searchParams.get("focus") !== "location") return undefined;
    const sid = searchParams.get("shop");
    if (sid && shops.some((s) => s.shop_id === sid)) setActiveShopId(sid);
    setTab("shop");
    searchParams.delete("focus"); searchParams.delete("shop");
    setSearchParams(searchParams, { replace: true });
    let tries = 0;
    const go = (el) => el.scrollIntoView({ behavior: "smooth", block: "center" });
    const poll = setInterval(() => {
      const el = document.querySelector('[data-testid="shop-pin-picker"]');
      if (el) { clearInterval(poll); go(el); setTimeout(() => go(el), 700); } else if (++tries > 40) clearInterval(poll);
    }, 150);
    return () => clearInterval(poll);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authChecking || loading) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-slate-500">Loading dashboard...</div>;
  if (!isVendor) return null;

  const activeShop = shops.find((s) => s.shop_id === activeShopId);
  const forceRefresh = () => setRefreshKey((k) => k + 1);
  // Wait for the async Shop Profile editor, then centre "Detect location" (clear of the sticky header); re-centre after images settle.
  const scrollToLocation = () => {
    let tries = 0;
    const go = () => document.querySelector('[data-testid="shop-pin-picker"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    const poll = setInterval(() => {
      if (document.querySelector('[data-testid="shop-pin-picker"]')) { clearInterval(poll); go(); setTimeout(go, 700); }
      else if (++tries > 40) clearInterval(poll);
    }, 150);
  };
  const openShopField = (id) => {
    setTab("shop");
    let tries = 0;
    const poll = setInterval(() => {
      const el = document.querySelector(`[data-testid="${id}"]`);
      if (el) { clearInterval(poll); el.scrollIntoView({ behavior: "smooth", block: "center" }); if (el.matches("input,textarea")) setTimeout(() => el.focus({ preventScroll: true }), 400); }
      else if (++tries > 40) clearInterval(poll);
    }, 150);
  };
  const scrollToTestId = (id) => setTimeout(() => document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 700);

  const renderSection = (id) => {
    if (id === "products") return activeShop && (
      <div className="space-y-6 min-w-0">
        <MyProducts shops={shops} onAdd={() => { setEditingProduct(null); setAddOpen(true); }} onEdit={(p) => { setEditingProduct(p); setAddOpen(true); }} refreshKey={refreshKey} onChanged={forceRefresh} />
      </div>
    );
    if (id === "shop") return activeShop && (
      <div className="space-y-6 min-w-0">
        <ShopStatusCard shopId={activeShop.shop_id} onChanged={forceRefresh} />
        {activeShop.business_profile_enabled && <BusinessProfileEditor shopId={activeShop.shop_id} />}
        <ShopProfileEditor shopId={activeShop.shop_id} onSaved={forceRefresh} />
      </div>
    );
    if (id === "services") return (
      <>
        <LocalDemandCard />
        <MyServices shops={shops} refreshKey={refreshKey} onChanged={forceRefresh} addSignal={serviceAddSignal} onAddHandled={() => setServiceAddSignal(0)} />
      </>
    );
    if (id === "analytics") return <VendorAnalytics refreshKey={refreshKey} />;
    if (id === "ads") return (
      <div className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6" data-testid="advertise-tab">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="min-w-0">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Megaphone className="w-5 h-5 text-emerald-700" /> {AM.title}</h2>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="advertise-tab-sub">{freeAds ? AM.tabSubFree : AM.tabSub}</p>
          </div>
          <button onClick={() => setAdvertiseOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm font-semibold flex-shrink-0" data-testid="advertise-new-btn">
            {AM.newAd}
          </button>
        </div>
        <AdModeNotice notice={modeNotice} onDismiss={closeModeNotice} place="ads" />
        <VendorAdsList refreshKey={adsRefreshKey} />
      </div>
    );
    return null;
  };

  const toolAction = (a) => {
    const tabs = { products: "products", services: "services", shop: "shop", ads: "ads", analytics: "analytics" };
    if (tabs[a]) { setTab(tabs[a]); return; }
    if (a === "add-product") { setEditingProduct(null); setAddOpen(true); return; }
    if (a === "add-service") { setTab("services"); setServiceAddSignal((n) => n + 1); return; }
    if (a === "shop-status") { setTab("shop"); scrollToTestId("shop-status-card"); return; }
    if (a === "advertise") { setTab("ads"); setAdvertiseOpen(true); return; }
    if (a === "analytics-wa") { setTab("analytics"); scrollToTestId("whatsapp-clicks"); return; }
    if (a === "reviews" && activeShop) { navigate(`/v/${activeShop.slug || activeShop.shop_id}`); return; }
    if (a === "profile") { navigate("/profile"); return; }
    if (a === "customer-mode") { setMode("customer"); toast.success("Switched to Customer mode"); navigate("/"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 lg:py-10 min-w-0 overflow-x-clip" data-testid="vendor-dashboard">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-4"><ArrowLeft className="w-4 h-4 rtl-flip" /> Back to home</Link>

      <DashboardTop user={user} shops={shops} activeShop={activeShop} activeShopId={activeShopId} setActiveShopId={setActiveShopId}
        hasLoc={!!activeShop && hasValidLocation(activeShop)} onToolBag={() => setToolBagOpen(true)} onShare={() => setShareOpen(true)}
        onSetLocation={() => { setTab("shop"); scrollToLocation(); }} openShopField={openShopField} />
      {tab !== "ads" && <AdModeNotice notice={modeNotice} onDismiss={closeModeNotice} place="dashboard" />}
      <div className="mb-5"><ReceivedLocations><SendLocationToCustomer /></ReceivedLocations></div>


      {shops.length === 0 ? (
        pro ? (
          <div className="bg-white border border-slate-100 rounded-2xl p-6 sm:p-8" data-testid="professional-dashboard-card">
            <Briefcase className="w-9 h-9 text-emerald-700 mb-3" />
            <div className="font-display text-xl font-bold text-slate-900">Professional service listing</div>
            <p className="text-sm text-slate-500 mt-1">Aap bina dukaan ke professional ke roop mein listed hain. Apni listing yahan se manage karein.</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Link to="/onboarding" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm font-semibold min-h-[40px]" data-testid="pro-manage-listing-btn"><Settings className="w-4 h-4" /> Manage professional listing</Link>
              {pro.public && <Link to={`/p/${user.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50 px-4 py-2 text-sm font-semibold min-h-[40px]" data-testid="pro-public-profile-btn"><ExternalLink className="w-4 h-4" /> View public profile</Link>}
            </div>
          </div>
        ) : (
        <div className="bg-white border border-slate-100 rounded-2xl p-10 text-center">
          <Store className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-xl font-bold">No shops assigned yet</div>
          <p className="text-sm text-slate-500 mt-1">Contact admin to link a shop to your vendor account.</p>
        </div>
        )
      ) : (
        <div className="space-y-3 min-w-0" data-testid="vendor-nav">
          {/* Single-open accordion: one `tab` state; clicking the open section closes it. */}
          {TABS.map((t) => {
            const Icon = t.icon;
            const open = tab === t.id;
            return (
              <section key={t.id} className={`bg-white border rounded-2xl shadow-sm min-w-0 scroll-mt-32 ${open ? "border-emerald-200" : "border-slate-200/80"}`} data-testid={`vendor-section-${t.id}`}>
                <button
                  type="button"
                  onClick={() => setTab(open ? null : t.id)}
                  aria-expanded={open}
                  aria-controls={`vendor-panel-${t.id}`}
                  className={`w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 min-h-[52px] rounded-2xl text-left transition-colors ${open ? "bg-emerald-50/60" : "hover:bg-slate-50"}`}
                  data-testid={`vendor-tab-${t.id}`}
                >
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${open ? "bg-emerald-700 text-white" : "bg-emerald-50 text-emerald-700"}`}><Icon className="w-4 h-4" /></span>
                  <span className="flex-1 min-w-0 font-display text-base sm:text-lg font-bold text-slate-900 truncate"><T k={t.label} /></span>
                  <ChevronDown className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div id={`vendor-panel-${t.id}`} className="px-2 sm:px-4 pb-4 pt-1 min-w-0" data-testid={`vendor-panel-${t.id}`}>
                    {renderSection(t.id)}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <AddProductDialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setEditingProduct(null); }} shops={shops} product={editingProduct} onCreated={forceRefresh} onSaved={forceRefresh} />
      <VendorToolBag open={toolBagOpen} onOpenChange={(o) => { setToolBagOpen(o); if (!o) setToolBagPanel({}); }} shopId={activeShopId} shops={shops} act={toolAction} onStockChanged={forceRefresh} initialPanel={toolBagPanel.panel} returnTo={toolBagPanel.ret} />
      <AdvertiseDialog open={advertiseOpen} onOpenChange={(o) => { setAdvertiseOpen(o); if (!o) setAdsRefreshKey((k) => k + 1); }} shops={shops} activeShopId={activeShopId} />
      {activeShop && (
        <ShareSheet
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          shopName={activeShop.name}
          tagline={activeShop.tagline || activeShop.description}
          shareUrl={`${window.location.origin}/v/${activeShop.slug || activeShop.shop_id}`}
          profileUrl={`${window.location.origin}/v/${activeShop.slug || activeShop.shop_id}`}
          shop={activeShop}
        />
      )}
    </div>
  );
}
